import { AzureGroup } from '@mnspro-cloud/services';
import { GroupRole } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import type { TFunction } from 'i18next';
import { GroupListItem } from '../GroupPicker/GroupPicker';

/**
 * Returns a localized label for a GroupRole value. Uses the shared
 * `groups:groupRole:*` namespace; see also web-app's `useGroupRoleLabel` hook
 * which exposes the same labels via React context.
 */
export const groupRoleLabel = (role: GroupRole, t: TFunction): string => {
  switch (role) {
    case GroupRole.course:
      return t('groups:groupRole:course', { defaultValue: 'Kurs' });
    case GroupRole.class:
      return t('groups:groupRole:class', { defaultValue: 'Klasse' });
    case GroupRole.workgroup:
      return t('groups:groupRole:workgroup', { defaultValue: 'Arbeitsgruppe' });
    case GroupRole.devices:
      return t('groups:groupRole:devices', { defaultValue: 'Geräte' });
    case GroupRole.exam:
      return t('groups:groupRole:exam', { defaultValue: 'Prüfung' });
    case GroupRole.none:
    default:
      return t('groups:groupRole:none', { defaultValue: 'Nicht vorhanden' });
  }
};

/**
 * Builds a GroupListItem for the GroupPicker from an AzureGroup, displaying
 * mail address and localized role in the secondary content line. When
 * expectedRoles is provided and the group's role is not in the list, the item
 * is marked disabled so the user can see the mismatch instead of an empty
 * list. Pass a single GroupRole or an array of allowed roles.
 */
export const buildAzureGroupListItem = (
  group: AzureGroup,
  expectedRoles: GroupRole | GroupRole[] | undefined,
  t: TFunction,
): GroupListItem => {
  const roleText = group.role !== undefined ? groupRoleLabel(group.role, t) : undefined;
  const mail = group.mailAddress && group.mailAddress.length > 0 ? group.mailAddress : undefined;
  const timePeriod =
    group.timePeriod && group.timePeriod.length > 0 ? group.timePeriod : undefined;
  const content = [mail, roleText, timePeriod]
    .filter((part): part is string => part !== undefined)
    .join(' · ');

  const allowed =
    expectedRoles === undefined
      ? undefined
      : Array.isArray(expectedRoles)
        ? expectedRoles
        : [expectedRoles];
  const isMismatch =
    allowed !== undefined && group.role !== undefined && !allowed.includes(group.role);

  return {
    id: group.id,
    name: group.name,
    mailAddress: group.mailAddress,
    content: content.length > 0 ? content : undefined,
    disabled: isMismatch,
  };
};
